import clsx from "clsx";
import { type ComponentProps, useCallback, useState } from "react";

import type { ComponentOrTag, DynamicProps } from "@shared/types";

import { Button } from "../button";
import { Icon } from "../icon";
import { Typography } from "../typography";

import s from "./accordion.module.scss";

type AccordionType<Element extends ComponentOrTag<ComponentProps<Element>>> =
  DynamicProps<Element> & {
    className?: string;
    title: string;
    children: React.ReactNode;
    activeAccordion?: string;
    onAccordionClick?: (id: string) => void;
    id: string;
    isOnceOpen?: boolean;
  };

//@ts-expect-error
export const Accordion = (props: AccordionType) => {
  const {
    id,
    title,
    tag = "p",
    children,
    className,
    activeAccordion,
    onAccordionClick,
    isOnceOpen = false,
    titleClassName,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);
  };

  const handleOnAccordionClick = useCallback(() => {
    if (isOnceOpen) {
      onAccordionClick(id);
    } else {
      toggleAccordion();
    }
  }, [id, isOnceOpen]);

  return (
    <div className={clsx(s.root, className)}>
      <Button className={s.button} onClick={handleOnAccordionClick}>
        <Typography tag={tag} weight="semiBold" className={titleClassName}>
          {title}
        </Typography>
        <Icon
          name="arrow-down"
          className={clsx(s.icon, {
            [s.rotate]: isOpen || activeAccordion === id,
          })}
        />
      </Button>
      <div
        className={clsx(s.content, {
          [s.viewContent]: isOpen || activeAccordion === id,
        })}
      >
        <div className={s.body}>
          <div className={s.inner}>{children}</div>
        </div>
      </div>
    </div>
  );
};

Accordion.displayName = "Accordion";
